/**
 * `2f` — formulário de lançamento e edição de oportunidade.
 *
 * 🔒 **BR-20 vem do CATÁLOGO, não daqui.** Quais campos são obrigatórios em cada
 * fase está em `catalogoStatus[].exigeCampos`, servido pela API. Esta tela lê a
 * lista e marca o asterisco — ela não tem uma cópia da regra. Se a iOX criar um
 * status novo em Configurações globais, o formulário obedece sem deploy.
 *
 * 🔒 **O servidor é o autoritativo.** A marcação aqui existe para o formulário
 * parecer justo, e nunca como o controle: uma transição bloqueada volta 409 com
 * `camposFaltantes`, e é ESSA resposta que marca os campos em vermelho.
 *
 * ⚠️ **Sem react-hook-form.** O formulário é controlado com `useState` puro. A
 * validação que importa é a do servidor (o gate do BR-20), e o que sobra é
 * "campo tem valor?" — que não justifica 12 kB gz e uma API nova para o time
 * aprender. Se surgir validação cruzada de verdade, revisitar.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import type { Oportunidade } from '@/api/contracts/oportunidade'
import {
  useCatalogoDeServicos,
  useCatalogoDeStatus,
  useContasAws,
  useOportunidade,
  useSalvarOportunidade,
  useTransicionarStatus,
} from '@/api/queries/finops'
import { codigoDoErro, detalhesDoErro, mensagemDoErro } from '@/components/erros'
import { EsqueletoDeFormulario } from '@/components/esqueleto'
import { EstadoDeErro } from '@/components/estados'
import {
  AreaDeTexto,
  BadgeDeStatus,
  Botao,
  Campo,
  Entrada,
  Selecao,
  Switch,
} from '@/components/primitivos'
import { formatarData, formatarUsd } from '@/format'
import { useClienteAtual } from '@/tenant/useClienteAtual'

/** Coluna do catálogo -> campo do formulário. `exigeCampos` fala snake_case. */
const CAMPO_DA_COLUNA: Record<string, string> = {
  account_id: 'accountId',
  servico_aws: 'servicoAws',
  recurso: 'recurso',
  descricao: 'descricao',
  custo_mensal_atual: 'custoMensalAtual',
  custo_mensal_potencial: 'custoMensalPotencial',
  data_proposta: 'dataProposta',
  data_implementacao: 'dataImplementacao',
  custo_mensal_apos_implementacao: 'custoMensalAposImplementacao',
  data_fim_vigencia: 'dataFimVigencia',
}

type Rascunho = {
  accountId: string
  servicoAws: string
  recurso: string
  descricao: string
  dataProposta: string
  dataImplementacao: string
  dataFimVigencia: string
  custoMensalAtual: string
  custoMensalPotencial: string
  custoMensalAposImplementacao: string
}

const VAZIO: Rascunho = {
  accountId: '',
  servicoAws: '',
  recurso: '',
  descricao: '',
  dataProposta: '',
  dataImplementacao: '',
  dataFimVigencia: '',
  custoMensalAtual: '',
  custoMensalPotencial: '',
  custoMensalAposImplementacao: '',
}

function paraRascunho(o: Oportunidade): Rascunho {
  return {
    accountId: o.accountId ?? '',
    servicoAws: o.servicoAws ?? '',
    recurso: o.recurso ?? '',
    descricao: o.descricao ?? '',
    dataProposta: o.dataProposta ?? '',
    dataImplementacao: o.dataImplementacao ?? '',
    dataFimVigencia: o.dataFimVigencia ?? '',
    custoMensalAtual: o.custoMensalAtual === null ? '' : String(o.custoMensalAtual),
    custoMensalPotencial: o.custoMensalPotencial === null ? '' : String(o.custoMensalPotencial),
    custoMensalAposImplementacao:
      o.custoMensalAposImplementacao === null ? '' : String(o.custoMensalAposImplementacao),
  }
}

/** `''` vira `null`, e não `0`. Campo em branco é AUSENTE, não zero. */
function numero(texto: string): number | null {
  if (texto.trim() === '') return null
  const valor = Number(texto.replace(',', '.'))
  return Number.isFinite(valor) ? valor : null
}

function texto(valor: string): string | null {
  return valor.trim() === '' ? null : valor
}

/**
 * Carrega a oportunidade e só então monta o formulário.
 *
 * ⚠️ A separação existe para que o estado do formulário nasça JÁ preenchido, via
 * `useState(() => …)`, em vez de ser preenchido por um `useEffect` depois do
 * primeiro render. Um `setState` dentro de efeito renderiza o formulário vazio e
 * o repinta — o que pisa no que o usuário digitou se a query revalidar, e o
 * eslint (`react-hooks/set-state-in-effect`) trata como erro, com razão.
 *
 * A `key` garante o remount ao trocar de oportunidade: sem ela, navegar de
 * OP-0001 para OP-0002 manteria o rascunho da primeira.
 */
export function OportunidadeFormulario() {
  const idCliente = useClienteAtual()
  const { idOportunidade } = useParams<{ idOportunidade: string }>()
  const criando = idOportunidade === 'nova' || idOportunidade === undefined

  const consulta = useOportunidade(idCliente, criando ? undefined : idOportunidade)

  if (criando) return <Formulario chave="nova" />
  if (consulta.isPending) return <EsqueletoDeFormulario rotulo="Carregando oportunidade…" />
  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }
  return (
    <Formulario key={consulta.data.idOportunidade} chave="edicao" oportunidade={consulta.data} />
  )
}

function Formulario({
  chave,
  oportunidade,
}: {
  chave: 'nova' | 'edicao'
  oportunidade?: Oportunidade
}) {
  const idCliente = useClienteAtual()
  const navegar = useNavigate()
  const criando = chave === 'nova'

  const contas = useContasAws(idCliente)
  const servicos = useCatalogoDeServicos(idCliente)
  const catalogo = useCatalogoDeStatus(idCliente)

  const salvar = useSalvarOportunidade(idCliente)
  const transicionar = useTransicionarStatus(idCliente)

  // Inicializador lazy: o estado JÁ nasce com o valor certo.
  const [rascunho, setRascunho] = useState<Rascunho>(() =>
    oportunidade ? paraRascunho(oportunidade) : VAZIO,
  )

  const statusAtual = oportunidade?.status ?? 'rascunho'
  const statusDoCatalogo = catalogo.data?.itens.find((s) => s.idStatus === statusAtual)

  const proximos = useMemo(() => statusDoCatalogo?.transicoesPermitidas ?? [], [statusDoCatalogo])

  /**
   * Os campos que o PRÓXIMO status vai exigir.
   *
   * Marcamos o asterisco pelo DESTINO, e não pelo status atual: em `rascunho`
   * tudo é opcional (BR-20), e um formulário sem nenhum asterisco não ajudaria
   * ninguém a saber o que preencher para avançar.
   */
  const exigidosAdiante = useMemo(() => {
    const destinos = catalogo.data?.itens.filter((s) => proximos.includes(s.idStatus)) ?? []
    const colunas = new Set(destinos.flatMap((s) => s.exigeCampos))
    return new Set([...colunas].map((c) => CAMPO_DA_COLUNA[c]).filter(Boolean) as string[])
  }, [catalogo.data, proximos])

  /**
   * 🔒 Os campos que o SERVIDOR disse que faltam, na última transição recusada.
   *
   * Vem de `detalhes.camposFaltantes` do 409 — não é validação local. É o que
   * permite marcar os campos exatos em vez de mostrar "erro de validação".
   */
  const camposFaltantes = useMemo(() => {
    if (codigoDoErro(transicionar.error) !== 'TRANSICAO_BLOQUEADA') {
      return new Map<string, string>()
    }
    const lista = (detalhesDoErro(transicionar.error).camposFaltantes ?? []) as {
      campo: string
      rotulo: string
    }[]
    return new Map(lista.map((c) => [c.campo, c.rotulo]))
  }, [transicionar.error])

  const erroDe = (campo: keyof Rascunho) =>
    camposFaltantes.has(campo) ? 'Obrigatório para avançar de fase.' : undefined

  // Os DOIS campos calculados. Read-only na tela porque o servidor é quem
  // calcula — e, no Postgres, são colunas GERADAS que um UPDATE nem pode citar.
  const economiaPotencial = (() => {
    const atual = numero(rascunho.custoMensalAtual)
    const potencial = numero(rascunho.custoMensalPotencial)
    return atual === null || potencial === null ? null : atual - potencial
  })()

  function corpoDoRascunho() {
    return {
      accountId: rascunho.accountId,
      servicoAws: rascunho.servicoAws,
      recurso: rascunho.recurso,
      descricao: rascunho.descricao,
      dataProposta: texto(rascunho.dataProposta),
      dataImplementacao: texto(rascunho.dataImplementacao),
      dataFimVigencia: texto(rascunho.dataFimVigencia),
      custoMensalAtual: numero(rascunho.custoMensalAtual),
      custoMensalPotencial: numero(rascunho.custoMensalPotencial),
      custoMensalAposImplementacao: numero(rascunho.custoMensalAposImplementacao),
    }
  }

  return (
    <section className="border-border-subtle bg-surface-card rounded-card flex w-full max-w-[760px] flex-col gap-6 border p-7">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="type-h4 text-text-primary">
            {criando ? 'Lançar oportunidade' : 'Editar oportunidade'}
          </h2>
          {oportunidade && (
            <BadgeDeStatus
              status={oportunidade.status}
              rotulo={statusDoCatalogo?.rotulo ?? oportunidade.status}
            />
          )}
        </div>
        {oportunidade && (
          <span className="text-text-muted type-sm">
            {oportunidade.codigo} · lançada em {formatarData(oportunidade.dataLancamento)} · origem:{' '}
            {oportunidade.origem === 'automatico' ? 'automática' : 'manual'}
          </span>
        )}
      </div>

      {/* Linha 1 do wireframe: Cliente, Account ID e Serviço AWS. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Campo rotulo="Cliente" obrigatorio>
          {/* O tenant vem do seletor global (BR-19) e não é escolhido aqui — o
              servidor o toma do escopo da requisição, nunca do corpo. */}
          <Entrada value={idCliente} readOnly disabled />
        </Campo>

        <Campo
          rotulo="Account ID"
          obrigatorio={exigidosAdiante.has('accountId')}
          hint="contas cadastradas no módulo"
          erro={erroDe('accountId')}
        >
          {/* 🔒 BR-24: catálogo, jamais texto livre. */}
          <Selecao
            value={rascunho.accountId}
            opcoes={(contas.data?.itens ?? []).map((c) => ({
              valor: c.accountId,
              rotulo: `${c.accountId} — ${c.apelido}`,
            }))}
            onChange={(e) => setRascunho((r) => ({ ...r, accountId: e.target.value }))}
          />
        </Campo>

        <Campo
          rotulo="Serviço AWS"
          obrigatorio={exigidosAdiante.has('servicoAws')}
          hint="lista editável na Administração"
          erro={erroDe('servicoAws')}
        >
          <Selecao
            value={rascunho.servicoAws}
            opcoes={(servicos.data?.itens ?? []).map((s) => ({
              valor: s.idServico,
              rotulo: s.rotulo,
            }))}
            onChange={(e) => setRascunho((r) => ({ ...r, servicoAws: e.target.value }))}
          />
        </Campo>
      </div>

      <Campo rotulo="Recurso" obrigatorio={exigidosAdiante.has('recurso')} erro={erroDe('recurso')}>
        <Entrada
          value={rascunho.recurso}
          placeholder="i-0a3f9c2e (m5.2xlarge)"
          onChange={(e) => setRascunho((r) => ({ ...r, recurso: e.target.value }))}
        />
      </Campo>

      <Campo
        rotulo="Descrição da oportunidade de otimização"
        obrigatorio={exigidosAdiante.has('descricao')}
        erro={erroDe('descricao')}
      >
        <AreaDeTexto
          value={rascunho.descricao}
          onChange={(e) => setRascunho((r) => ({ ...r, descricao: e.target.value }))}
        />
      </Campo>

      {/* Linha 4: status (só leitura — muda por transição) e visibilidade. */}
      <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-3">
        <Campo rotulo="Status" hint="muda pelos botões de fase, abaixo">
          <Entrada value={statusDoCatalogo?.rotulo ?? statusAtual} readOnly disabled />
        </Campo>

        <Campo rotulo="Visibilidade para o cliente">
          <div className="flex items-center gap-2 py-2">
            {/* 🔒 BR-01 — muda por endpoint próprio, na tela 2e. Aqui é só o
                estado, para o arquiteto ver sem precisar sair do formulário. */}
            <Switch
              ligado={oportunidade?.visibilidadeCliente ?? false}
              rotulo="Visível ao cliente"
              desabilitado
              aoAlternar={() => undefined}
            />
            <span className="type-sm text-text-muted">
              {oportunidade?.visibilidadeCliente ? 'visível' : 'não visível'} · alterna na aba
              Oportunidades
            </span>
          </div>
        </Campo>
      </div>

      {/* Bloco bordado do wireframe: VALORES MENSAIS (USD). */}
      <fieldset className="border-border-subtle rounded-card flex flex-col gap-4 border p-5">
        <legend className="type-eyebrow text-text-muted px-2">Valores mensais (USD)</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Campo
            rotulo="Custo atual"
            obrigatorio={exigidosAdiante.has('custoMensalAtual')}
            erro={erroDe('custoMensalAtual')}
          >
            <Entrada
              inputMode="decimal"
              value={rascunho.custoMensalAtual}
              placeholder="2.140"
              onChange={(e) => setRascunho((r) => ({ ...r, custoMensalAtual: e.target.value }))}
            />
          </Campo>
          <Campo
            rotulo="Custo potencial"
            obrigatorio={exigidosAdiante.has('custoMensalPotencial')}
            erro={erroDe('custoMensalPotencial')}
          >
            <Entrada
              inputMode="decimal"
              value={rascunho.custoMensalPotencial}
              placeholder="1.250"
              onChange={(e) => setRascunho((r) => ({ ...r, custoMensalPotencial: e.target.value }))}
            />
          </Campo>
          <Campo rotulo="Economia potencial (calculada)">
            {/* 🔒 READ-ONLY. É coluna GERADA no banco: um UPDATE que a cite faz
                a instrução inteira falhar, e a API recusa com 400. */}
            <div className="rounded-field bg-gray-50 text-iox-navy border-border-subtle border px-3 py-2 type-body font-semibold">
              {economiaPotencial === null ? '—' : formatarUsd(economiaPotencial)}
            </div>
          </Campo>
        </div>
      </fieldset>

      {/* Bloco "CAMPOS POR FASE" — habilitados pelos gatilhos de status. */}
      <fieldset className="border-border-subtle bg-gray-25 rounded-card grid grid-cols-1 gap-4 border p-5 md:grid-cols-2">
        <legend className="type-eyebrow text-text-muted px-2">Campos por fase</legend>

        <Campo
          rotulo="Data proposta"
          hint="preenchida no gatilho → proposta"
          obrigatorio={exigidosAdiante.has('dataProposta')}
          erro={erroDe('dataProposta')}
        >
          <Entrada
            type="date"
            value={rascunho.dataProposta}
            onChange={(e) => setRascunho((r) => ({ ...r, dataProposta: e.target.value }))}
          />
        </Campo>

        <Campo
          rotulo="Data de fim de vigência da oportunidade"
          // A regra que mais confunde na tela: vazio NÃO é "esqueci", é
          // "não expira". Dizer isso aqui evita alguém preencher por precaução.
          hint="automática pela origem ou manual · vazio = a economia não expira"
        >
          <Entrada
            type="date"
            value={rascunho.dataFimVigencia}
            onChange={(e) => setRascunho((r) => ({ ...r, dataFimVigencia: e.target.value }))}
          />
        </Campo>

        <Campo
          rotulo="Data de implementação"
          hint="gatilho → implementada"
          obrigatorio={exigidosAdiante.has('dataImplementacao')}
          erro={erroDe('dataImplementacao')}
        >
          <Entrada
            type="date"
            value={rascunho.dataImplementacao}
            onChange={(e) => setRascunho((r) => ({ ...r, dataImplementacao: e.target.value }))}
          />
        </Campo>

        <Campo
          rotulo="Custo após implementação"
          hint="medido; a economia realizada é calculada a partir dele"
          obrigatorio={exigidosAdiante.has('custoMensalAposImplementacao')}
          erro={erroDe('custoMensalAposImplementacao')}
        >
          <Entrada
            inputMode="decimal"
            value={rascunho.custoMensalAposImplementacao}
            placeholder="—"
            onChange={(e) =>
              setRascunho((r) => ({ ...r, custoMensalAposImplementacao: e.target.value }))
            }
          />
        </Campo>
      </fieldset>

      <p className="text-text-muted rounded-field bg-gray-50 px-4 py-3 type-sm">
        <i className="ph ph-asterisk mr-2" aria-hidden="true" />
        Em <strong>rascunho</strong> todos os campos são opcionais. Os campos marcados com
        <strong> *</strong> passam a ser obrigatórios na próxima mudança de fase — o servidor recusa
        a transição e diz exatamente qual campo falta.
      </p>

      {(salvar.isError || transicionar.isError) && (
        <div
          role="alert"
          className="rounded-field border-danger-500/30 bg-danger-100 text-danger-700 border px-4 py-3 type-sm"
        >
          {/* A mensagem do SERVIDOR, e não uma genérica: é ela que nomeia os
              campos faltantes e a transição recusada. */}
          {mensagemDoErro(salvar.error ?? transicionar.error)}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Só as transições que o CATÁLOGO permite a partir do status atual.
            Um botão para uma fase inalcançável só ensinaria o usuário a receber
            409. */}
        <div className="flex flex-wrap items-center gap-2">
          {!criando &&
            proximos.map((destino) => {
              const rotulo =
                catalogo.data?.itens.find((s) => s.idStatus === destino)?.rotulo ?? destino
              return (
                <Botao
                  key={destino}
                  variante={destino === 'arquivada' ? 'secundaria' : 'accent'}
                  disabled={transicionar.isPending}
                  onClick={() =>
                    transicionar.mutate({
                      idOportunidade: oportunidade!.idOportunidade,
                      novoStatus: destino,
                      // Manda o que está na tela JUNTO com a transição: é o
                      // fluxo real — o arquiteto preenche o que faltava e clica
                      // no botão da fase seguinte, numa requisição só.
                      campos: corpoDoRascunho(),
                    })
                  }
                >
                  mover para {rotulo.toLowerCase()}
                </Botao>
              )
            })}
        </div>

        <div className="flex items-center gap-2">
          {/* `relative: 'path'`: o `..` padrão sobe pela ÁRVORE de rotas, e
              `oportunidades/:id` é irmã de `oportunidades` — caía no índice do
              módulo, o Dashboard (achado de 15/10/2026). */}
          <Botao variante="secundaria" onClick={() => navegar('..', { relative: 'path' })}>
            cancelar
          </Botao>
          <Botao
            variante="primaria"
            disabled={salvar.isPending}
            onClick={() =>
              salvar.mutate(
                {
                  ...(criando ? {} : { idOportunidade: oportunidade!.idOportunidade }),
                  ...corpoDoRascunho(),
                } as never,
                { onSuccess: () => navegar('..', { relative: 'path' }) },
              )
            }
          >
            {salvar.isPending ? 'salvando…' : 'salvar oportunidade'}
          </Botao>
        </div>
      </div>
    </section>
  )
}
